import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import { GroupedViewLite } from '../GroupedViewLite'
import { useTablePersistence } from '../useTablePersistence'
import { exportToCSV } from '../exportCSV'
import {
  SortState,
  FilterState,
  PaginationState,
  SelectedColumn,
} from '../types'
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_STATE } from '../defaultsFromLegacy'
import { normalizeFilterInputType, augmentSyncStatusFilterOptions } from '../filterUtils'
import {
  getAgProductColumnsAsync,
  getListingAgProductsAsync,
} from 'src/redux/actions/omsProductActions'
import { getSortOrder } from 'src/marketConnect/helpers'
import { isListingProductBulkActionPlatform } from 'src/utils/listingProductBulkActions'

interface GroupedViewLiteContainerProps {
  id?: string
  platformId?: string
  currentChannelData?: any
  mainStore?: boolean
  accountingStore?: boolean
  shippingStore?: boolean
  erpStore?: boolean
  tabsData?: any[]
  onLinkClick?: (rowData: any, rowIndex: number) => void
  linkingCompleted?: boolean
}

type CatalogColumn = {
  field: string
  headerName?: string
  label?: string
  name?: string
  width?: number
  sortable?: boolean
  resizable?: boolean
  filterable?: boolean
  allowVisibility?: string
  fixed?: boolean
  type?: string
  format?: string
  filterInputType?: string
  filterOptions?: Array<{ label: string; value: any }>
}

type GroupedRow = {
  groupKey: string
  groupLabel: string
  items: any[]
}

const DEFAULT_VISIBLE_FIELDS = [
  'name',
  'sku',
  'isCreatedByCommercium',
  'total_variants',
  'api_status',
  'total_available_quantity',
  'total_reserve_quantity',
  'created_at',
  'updated_at',
]

export const GroupedViewLiteContainer: FC<GroupedViewLiteContainerProps> = ({
  id,
  platformId,
  currentChannelData,
  mainStore = false,
  accountingStore = false,
  shippingStore = false,
  erpStore = false,
  tabsData = [],
  onLinkClick,
  linkingCompleted = false,
}) => {
  const dispatch = useAppDispatch()
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData)
  const groupedViewLiteSlice = useAppSelector((state: any) => state.groupedViewLiteSlice)

  const userId = fetchedUserAccountData?.id || 'guest'

  const persistence = useTablePersistence({
    userId,
    platformId: platformId || '0',
    channelId: id || '0',
  })

  const initialState = persistence.getInitialState()

  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>(
    initialState.selectedColumns || []
  )
  const [sortState, setSortState] = useState<SortState | null>(
    initialState.sortState || null
  )
  const [filterState, setFilterState] = useState<FilterState>(
    initialState.filterState || { globalSearch: '', columnFilters: [] }
  )
  const [paginationState, setPaginationState] = useState<PaginationState>({
    page: 1,
    pageSize: initialState.pageSize || DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  })
  const [selectedRowIds, setSelectedRowIds] = useState<Array<number | string>>([])
  const [selectedAction, setSelectedAction] = useState('')

  const mandatoryColumns = useMemo(() => ['name'], [])

  const productsRows = groupedViewLiteSlice?.products?.rows || []
  const productsColumns = groupedViewLiteSlice?.products?.columns || []
  const productColumns = groupedViewLiteSlice?.productColumns?.columns || []
  const total = groupedViewLiteSlice?.products?.total || 0

  const loading =
    Boolean(groupedViewLiteSlice?.products?.loading) ||
    Boolean(groupedViewLiteSlice?.productColumns?.loading)

  const error =
    groupedViewLiteSlice?.products?.error ||
    groupedViewLiteSlice?.productColumns?.error ||
    null

  const areSelectedColumnsEqual = useCallback((a: SelectedColumn[], b: SelectedColumn[]) => {
    if (a === b) return true
    if (a.length !== b.length) return false

    for (let i = 0; i < a.length; i += 1) {
      const left = a[i]
      const right = b[i]
      if (
        left.field !== right.field ||
        left.order !== right.order ||
        left.width !== right.width ||
        (left.pinned || null) !== (right.pinned || null)
      ) {
        return false
      }
    }

    return true
  }, [])

  useEffect(() => {
    persistence.markInitialized()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      total,
      totalPages: Math.ceil(total / prev.pageSize),
    }))
  }, [total])

  useEffect(() => {
    if (persistence.isInitialized()) {
      persistence.savePersistedState({
        selectedColumns,
        sortState,
        filterState,
        pageSize: paginationState.pageSize,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, sortState, filterState, paginationState.pageSize])

  useEffect(() => {
    if (!id) return

    dispatch(
      getAgProductColumnsAsync({
        platformId: Number(id),
        page: 1,
        limit: 10,
      }) as any
    )
  }, [dispatch, id])

  useEffect(() => {
    if (!id || linkingCompleted === true) return

    dispatch(
      getListingAgProductsAsync({
        platformId: Number(id),
        page: paginationState.page,
        limit: paginationState.pageSize,
        search_string: filterState.globalSearch || '',
        sort_by: sortState?.field || '',
        sort_order: getSortOrder(sortState?.direction),
      }) as any
    )
  }, [
    dispatch,
    id,
    paginationState.page,
    paginationState.pageSize,
    filterState.globalSearch,
    sortState,
    linkingCompleted,
  ])

  const rawColumns = productColumns.length > 0 ? productColumns : productsColumns

  const catalogColumns: CatalogColumn[] = useMemo(() => {
    return (rawColumns || []).map((col: any) => {
      const filterInputType = normalizeFilterInputType(col?.advance_option, col.field)
      return {
      field: col.field,
      headerName: col.headerName,
      label: col.headerName,
      name: col.headerName,
      width: col.field === 'name' ? 260 : 170,
      sortable: Boolean(col?.advance_option?.allow_sorting),
      resizable: Boolean(col?.advance_option?.allow_resize),
      filterable:
        filterInputType === 'DATE_RANGE' ||
        col.field === 'created_at' ||
        col.field === 'updated_at' ||
        Boolean(col?.advance_option?.allow_filter),
      allowVisibility: col?.allow_visiblity,
      fixed: col?.allow_visiblity === 'ALWAYS_VISIBLE',
      filterInputType,
      filterOptions: augmentSyncStatusFilterOptions(
        col.field,
        col?.advance_option?.filter_options || []
      ),
      filterOperators: Array.isArray(col?.advance_option?.filter_operators)
        ? col.advance_option.filter_operators
        : [],
      type:
        filterInputType === 'DATE_RANGE' ||
        col.field === 'created_at' ||
        col.field === 'updated_at'
          ? 'date'
          : 'text',
    }})
  }, [rawColumns])

  useEffect(() => {
    if (selectedColumns.length > 0 || catalogColumns.length === 0) return

    const defaults = catalogColumns
      .filter((col) => {
        if (!col.field) return false
        if (col.allowVisibility === 'NO') return false
        if (col.allowVisibility === 'ALWAYS_VISIBLE') return true
        return DEFAULT_VISIBLE_FIELDS.includes(col.field)
      })
      .map((col, index) => ({
        field: col.field,
        order: index,
      }))

    setSelectedColumns(defaults)
  }, [catalogColumns, selectedColumns.length])

  const getColumnByField = useCallback(
    (field: string) => catalogColumns.find((col) => col.field === field),
    [catalogColumns]
  )

  const handleColumnsChange = useCallback(
    (columns: SelectedColumn[]) => {
      setSelectedColumns((prev) =>
        areSelectedColumnsEqual(prev, columns) ? prev : columns
      )
    },
    [areSelectedColumnsEqual]
  )

  const handleReorderColumns = useCallback((dragIndex: number, hoverIndex: number) => {
    setSelectedColumns((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order)
      const dragged = sorted[dragIndex]
      if (!dragged) return prev

      sorted.splice(dragIndex, 1)
      sorted.splice(hoverIndex, 0, dragged)

      return sorted.map((item, index) => ({
        ...item,
        order: index,
      }))
    })
  }, [])

  const handlePinColumn = useCallback(
    (field: string, position: 'left' | 'right' | null) => {
      setSelectedColumns((prev) =>
        prev.map((col) =>
          col.field === field ? { ...col, pinned: position } : col
        )
      )
    },
    []
  )

  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortState({ field, direction })
    setPaginationState((prev) => ({ ...prev, page: 1 }))
  }, [])

  const handleFilterChange = useCallback((filters: FilterState) => {
    setFilterState(filters)
    setPaginationState((prev) => ({ ...prev, page: 1 }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setPaginationState((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPaginationState((prev) => ({ ...prev, pageSize, page: 1 }))
  }, [])

  const handleExportCSV = useCallback(() => {
    exportToCSV(productsRows, selectedColumns, getColumnByField as any, id || '0')
  }, [productsRows, selectedColumns, getColumnByField, id])

  const handleResetColumns = useCallback(() => {
    persistence.clearPersistedState()

    const defaults = catalogColumns
      .filter((col) => {
        if (!col.field) return false
        if (col.allowVisibility === 'NO') return false
        if (col.allowVisibility === 'ALWAYS_VISIBLE') return true
        return DEFAULT_VISIBLE_FIELDS.includes(col.field)
      })
      .map((col, index) => ({
        field: col.field,
        order: index,
      }))

    setSelectedColumns(defaults)
    setSortState(DEFAULT_SORT_STATE)
    setFilterState({ globalSearch: '', columnFilters: [] })
    setPaginationState((prev) => ({ ...prev, page: 1 }))
  }, [catalogColumns, persistence])

  const refetch = useCallback(() => {
    if (!id) return

    dispatch(
      getListingAgProductsAsync({
        platformId: Number(id),
        page: paginationState.page,
        limit: paginationState.pageSize,
        search_string: filterState.globalSearch || '',
        sort_by: sortState?.field || '',
        sort_order: getSortOrder(sortState?.direction),
      }) as any
    )
  }, [dispatch, id, paginationState.page, paginationState.pageSize, filterState.globalSearch, sortState])

  const enableBulkActions =
    !mainStore &&
    !erpStore &&
    isListingProductBulkActionPlatform(currentChannelData?.self_data?.name)

  const groupedLiteData: GroupedRow[] = useMemo(() => {
    const rows = Array.isArray(productsRows) ? productsRows : []

    const groupedMap = rows.reduce((acc: Record<string, any[]>, item: any) => {
      const groupKey = item?.name || item?.sku || 'Ungrouped'
      if (!acc[groupKey]) acc[groupKey] = []
      acc[groupKey].push(item)
      return acc
    }, {})

    return Object.entries(groupedMap).map(([groupKey, items]) => ({
      groupKey,
      groupLabel: groupKey,
      items,
    }))
  }, [productsRows])

  useEffect(() => {
  console.log('🔍 GROUPED VIEW DEBUG', {
    redux: {
      products: groupedViewLiteSlice?.products,
      productColumns: groupedViewLiteSlice?.productColumns,
    },

    derived: {
      productsRowsLength: productsRows?.length,
      productsColumnsLength: productsColumns?.length,
      productColumnsLength: productColumns?.length,
      total,
    },

    grouping: {
      groupedLiteDataLength: groupedLiteData?.length,
      groupedLiteDataSample: groupedLiteData?.[0],
    },

    state: {
      filterState,
      sortState,
      selectedColumns,
      paginationState,
    },
  })
}, [
  groupedViewLiteSlice,
  productsRows,
  productsColumns,
  productColumns,
  groupedLiteData,
  filterState,
  sortState,
  selectedColumns,
  paginationState,
  total,
])

  if (error) {
    return (
      <div className='alert alert-danger d-flex align-items-center mb-4' role='alert'>
        <i className='bi bi-exclamation-triangle-fill me-3 fs-3'></i>
        <div>
          <strong>Error loading grouped lite data:</strong>
          <div className='mt-1'>{error}</div>
        </div>
      </div>
    )
  }

  return (
    <GroupedViewLite
      data={groupedLiteData}
      loading={loading}
      catalogColumns={catalogColumns as any}
      selectedColumns={selectedColumns}
      onColumnsChange={handleColumnsChange}
      onReorderColumns={handleReorderColumns}
      onPinColumn={handlePinColumn}
      getColumnByField={getColumnByField as any}
      sortState={sortState}
      onSortChange={handleSortChange}
      filterState={filterState}
      onFilterChange={handleFilterChange}
      paginationState={paginationState}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onRefresh={refetch}
      onExportCSV={handleExportCSV}
      onResetColumns={handleResetColumns}
      platformId={platformId}
      channelId={id}
      mainStore={mainStore}
      erpStore={erpStore}
      tabsData={tabsData}
      onLinkClick={onLinkClick}
      enableBulkActions={enableBulkActions}
      selectedAction={selectedAction}
      onSelectedActionChange={setSelectedAction}
      selectedCount={selectedRowIds.length}
      selectionEnabled={enableBulkActions}
      selectedRowIds={selectedRowIds}
      onSelectedRowIdsChange={setSelectedRowIds}
      mandatoryColumns={mandatoryColumns}
      defaultVisibleFields={DEFAULT_VISIBLE_FIELDS}
      onOpenColumnsConfig={() => {}}
    />
  )
}

export default GroupedViewLiteContainer